/**
 * State-tax pricing: the one source (spec 2026-09-30, sections 4.1 and 5.1).
 * Every amount is integer cents. Stripe price ids are not here; they stay in
 * `apps/api/src/state-tax/pricing.ts`, which reads its amounts from this file.
 *
 * Owner decisions (2026-09-30): keep the $89 service fee, charge no late fee
 * of our own, pass state penalties through at cost, and pass the card fee
 * through on Delaware corporation state amounts.
 */

import type { EntityKind, StateCode } from "./entity";
import { DE_ANNUAL_REPORT_FEE_CENTS } from "./franchise-tax";

/** Our fee for preparing and filing one state report. */
export const SERVICE_FEE_CENTS = 8_900;

/**
 * Wyoming annual report state fee. $60 license tax plus the online surcharge.
 * OWNER TO CONFIRM: the registry's surcharge is 2.4% with a $1 minimum, which
 * would make $61.44 for the minimum report, not $62.50. The Stripe price and
 * every customer screen use $62.50 today, so this keeps it until confirmed.
 */
export const WY_STATE_FEE_CENTS = 6_250;

/** Delaware LLC annual tax through tax year 2025 ($300, eCorp confirmed for TY2025). */
export const DE_LLC_STATE_FEE_CENTS = 30_000;
/** Delaware LLC annual tax from tax year 2026 ($400; 6 Del. C. 18-1107(b), HB 400). */
export const DE_LLC_STATE_FEE_2026_CENTS = 40_000;
/** First tax year the Delaware LLC annual tax is $400. */
export const DE_LLC_FEE_INCREASE_TAX_YEAR = 2026;

/**
 * The card processing fee passed through on Delaware corporation state
 * amounts. A named constant, not a setting: the operator no longer types a
 * rate (the old copies said 3%, "any rate" and 2.9% + 30 cents).
 */
export const CARD_FEE_RATE = 0.03;

/** The Delaware LLC annual tax for a tax year. */
export function deLlcStateFeeCents(taxYear: number): number {
  return taxYear >= DE_LLC_FEE_INCREASE_TAX_YEAR
    ? DE_LLC_STATE_FEE_2026_CENTS
    : DE_LLC_STATE_FEE_CENTS;
}

/** `amount` x CARD_FEE_RATE in cents, rounded half up. */
export function cardFee(amountCents: number): number {
  if (!Number.isInteger(amountCents) || amountCents < 0) {
    throw new Error(`cardFee needs a non-negative integer, got ${amountCents}`);
  }
  // 3% = 3/100: integer math avoids 0.03 x n floating-point drift.
  const ratePercent = Math.round(CARD_FEE_RATE * 100);
  return Math.floor((amountCents * ratePercent + 50) / 100);
}

export interface PricingInput {
  state: StateCode;
  entity: EntityKind;
  taxYear: number;
  /** Full Compliance covers the $89 service fee (the state fee is still due). */
  waived: boolean;
  /** Required for a Delaware corporation: a `franchiseTax()` result, or `{ tax }` in cents. */
  franchise?: { tax: number };
  /** State penalty and interest passed through at cost, in cents. Default 0. */
  lateCharges?: number;
}

export interface PricingResult {
  serviceFee: number;
  /** The state's tax (plus the $50 report fee for a Delaware corporation). */
  stateFee: number;
  lateCharges: number;
  cardFee: number;
  total: number;
}

/**
 * What an order charges. The card fee applies only to a Delaware corporation,
 * on its state fee plus late charges; the service fee never carries one.
 */
export function pricing(input: PricingInput): PricingResult {
  const serviceFee = input.waived ? 0 : SERVICE_FEE_CENTS;
  const late = input.lateCharges ?? 0;
  const isDeCorporation =
    input.state === "DE" && input.entity === "corporation";

  let stateFee: number;
  if (input.state === "WY") {
    stateFee = WY_STATE_FEE_CENTS;
  } else if (isDeCorporation) {
    if (!input.franchise) {
      throw new Error(
        "pricing: a Delaware corporation needs its franchise tax figure",
      );
    }
    stateFee = input.franchise.tax + DE_ANNUAL_REPORT_FEE_CENTS;
  } else {
    stateFee = deLlcStateFeeCents(input.taxYear);
  }

  const card = isDeCorporation ? cardFee(stateFee + late) : 0;
  return {
    serviceFee,
    stateFee,
    lateCharges: late,
    cardFee: card,
    total: serviceFee + stateFee + late + card,
  };
}
